/**
 * TurnCodeFixModal - Inline editor for fixing corrupted Turn Codes
 * 
 * Used when a Turn Code validation error is detected at render time.
 * Allows scan agents to fix the issue without leaving the content editor.
 * 
 * Uses the same Edge Function as admin tools for consistency.
 */

import React, { useState, useEffect } from 'react';
import { X, Save, AlertTriangle, Loader2, Check, HelpCircle } from 'lucide-react';
import { supabase } from '@/lib/supabase/client';
import { errorTracker } from '@/lib/logging';
import { useAuth } from '@/lib/auth';
import type { TurnCodeError } from '@/lib/turn-color';

interface TurnCodeFixModalProps {
  isOpen: boolean;
  onClose: () => void;
  error: TurnCodeError;
  onFixed: (word: string, newTurnCode: string) => void;
}

export function TurnCodeFixModal({ isOpen, onClose, error, onFixed }: TurnCodeFixModalProps) {
  const { profile } = useAuth();
  const [newTurnCode, setNewTurnCode] = useState(error.turnCode);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Check if user has admin access
  const isAdmin = !!profile?.admin_level && profile?.admin_is_active !== false;

  // Reset state when error changes
  useEffect(() => {
    setNewTurnCode(error.turnCode);
    setSaveError(null);
    setSaved(false);
  }, [error]);

  const handleSave = async () => {
    if (!isAdmin) {
      setSaveError('Admin access required to fix Turn Codes');
      return;
    }

    setSaving(true);
    setSaveError(null);

    try {
      // Get current session for auth header
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      // Use the same Edge Function as admin tools (with auth)
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/turn-color-sync`,
        {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ 
            action: 'save_word', 
            word: error.word.toLowerCase(), 
            turn_code: newTurnCode 
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to save');
      }

      // Log the fix for monitoring
      console.log(`[TurnCodeFix] Fixed "${error.word}": "${error.turnCode}" -> "${newTurnCode}"`);
      
      setSaved(true);
      onFixed(error.word, newTurnCode);
      
      // Auto-close after success
      setTimeout(() => {
        onClose();
      }, 1500);

    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setSaveError(message);
      
      // Log to error tracker
      errorTracker.dataIntegrity(
        `Failed to fix turn_code for "${error.word}": ${message}`,
        'turn_color_words',
        { metadata: { word: error.word, oldCode: error.turnCode, newCode: newTurnCode } }
      );
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
      <div className="bg-[#1C1C1E] rounded-2xl shadow-2xl w-full max-w-md border border-white/10">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-[#FF453A]/20">
              <AlertTriangle className="w-5 h-5 text-[#FF453A]" />
            </div>
            <div>
              <h3 className="text-white font-semibold">Fix Turn Code</h3>
              <p className="text-white/50 text-sm">Data corruption detected</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
          >
            <X className="w-5 h-5 text-white/50" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-4">
          {/* Error details */}
          <div className="p-4 rounded-xl bg-[#FF453A]/10 border border-[#FF453A]/20">
            <p className="text-[#FF453A] text-sm font-medium mb-2">Error:</p>
            <p className="text-white/70 text-sm">{error.error}</p>
          </div>

          {/* Word info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-white/50 text-xs uppercase tracking-wide">Word</label>
              <p className="text-white text-lg font-mono mt-1">{error.word}</p>
            </div>
            <div>
              <label className="text-white/50 text-xs uppercase tracking-wide">Extracted</label>
              <p className="text-white/70 text-lg font-mono mt-1">{error.extractedLetters}</p>
            </div>
          </div>

          {/* Current turn code */}
          <div>
            <label className="text-white/50 text-xs uppercase tracking-wide">Current Turn Code</label>
            <p className="text-white/50 text-sm font-mono mt-1 p-2 bg-white/5 rounded-lg break-all">
              {error.turnCode}
            </p>
          </div>

          {/* New turn code input */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-white/50 text-xs uppercase tracking-wide">New Turn Code</label>
              <button
                className="text-[#0A84FF] text-xs flex items-center gap-1 hover:underline"
                onClick={() => window.open('https://docs.google.com/spreadsheets/d/1TqNs5yXwu64sKLJ_8yOSIqzEMHIoubDd0Cn3-DBgvWc', '_blank')}
              >
                <HelpCircle className="w-3 h-3" />
                View Lexicon
              </button>
            </div>
            <input
              type="text"
              value={newTurnCode}
              onChange={(e) => setNewTurnCode(e.target.value)}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white font-mono text-sm focus:outline-none focus:border-[#0A84FF] focus:ring-1 focus:ring-[#0A84FF]"
              placeholder="e.g., f, [a-black], c, t"
            />
            <p className="text-white/30 text-xs mt-2">
              Format: consonants as plain letters, vowels as vowel g-p values [grapheme-color]. Separate with commas.
            </p>
          </div>

          {/* Admin-only notice */}
          {!isAdmin && (
            <div className="p-3 rounded-lg bg-[#FF9F0A]/10 border border-[#FF9F0A]/20">
              <p className="text-[#FF9F0A] text-sm">Admin access required to fix Turn Codes. Please contact an administrator.</p>
            </div>
          )}

          {/* Error message */}
          {saveError && (
            <div className="p-3 rounded-lg bg-[#FF453A]/10 border border-[#FF453A]/20">
              <p className="text-[#FF453A] text-sm">{saveError}</p>
            </div>
          )}

          {/* Success message */}
          {saved && (
            <div className="p-3 rounded-lg bg-[#30D158]/10 border border-[#30D158]/20 flex items-center gap-2">
              <Check className="w-4 h-4 text-[#30D158]" />
              <p className="text-[#30D158] text-sm">Turn code saved! Refresh preview to see changes.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-white/10">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-white/70 hover:bg-white/10 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || saved || newTurnCode === error.turnCode || !isAdmin}
            className="px-4 py-2 rounded-lg bg-[#0A84FF] text-white font-medium flex items-center gap-2 hover:bg-[#0A84FF]/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : saved ? (
              <>
                <Check className="w-4 h-4" />
                Saved
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Fix
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default TurnCodeFixModal;
